import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  CREATE_LIVESTREAM,
  ADD_LIVESTREAM_PRODUCT,
  GET_STORE_PRODUCTS
} from '../graphql/operations'
import { useAuth } from '../auth/AuthContext'

interface StoreProductsData {
  store: {
    id: string
    storeProducts: {
      id: string
      product: {
        id: string
        title: string
        imageUrls: string[] | null
        status: string
      }
    }[]
  } | null
}

export default function CreateStream() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  // Default to "30 minutes from now" so a freshly-created stream is
  // upcoming-not-overdue when the user hits Save.
  const [startedAt, setStartedAt] = useState(() => {
    const t = new Date(Date.now() + 30 * 60 * 1000)
    return t.toISOString().slice(0, 16)
  })
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: productsData } = useQuery<StoreProductsData>(GET_STORE_PRODUCTS, {
    variables: { storeId: user?.storeId },
    skip: !user?.storeId
  })

  const [createLivestream] = useMutation(CREATE_LIVESTREAM)
  const [addProduct] = useMutation(ADD_LIVESTREAM_PRODUCT)

  const products = (productsData?.store?.storeProducts ?? []).map(sp => sp.product)

  function toggleProduct(id: string) {
    setSelectedProductIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (!user?.storeId) {
      setError('You need a store to create a livestream.')
      return
    }

    setSubmitting(true)
    try {
      const isoStart = new Date(startedAt).toISOString()
      const { data } = await createLivestream({
        variables: {
          storeId: user.storeId,
          title,
          description: description || null,
          startedAt: isoStart
        }
      })

      const payload = data?.createLivestream
      const userErr = payload?.userErrors?.[0]?.message
      if (userErr) {
        setError(userErr)
        setSubmitting(false)
        return
      }
      const livestream = payload?.livestream
      if (!livestream) {
        setError('Server returned no livestream id.')
        setSubmitting(false)
        return
      }

      // Attach selected products in declared order. Failures are
      // non-fatal — the stream was created either way; we just warn.
      let attachFailures = 0
      const ids = [...selectedProductIds]
      for (let i = 0; i < ids.length; i++) {
        try {
          const result = await addProduct({
            variables: {
              livestreamId: livestream.id,
              productId: ids[i],
              position: i + 1
            }
          })
          const aErr = result.data?.livestreamProductAdd?.userErrors?.[0]?.message
          if (aErr) {
            console.warn('AddLivestreamProduct error', aErr)
            attachFailures += 1
          }
        } catch (err) {
          console.warn('AddLivestreamProduct threw', err)
          attachFailures += 1
        }
      }

      if (attachFailures > 0) {
        // Surface the partial success but continue navigation — the
        // creator can still go live without the broken attaches.
        alert(`Stream created. ${attachFailures} of ${ids.length} products couldn't be attached.`)
      }

      navigate(`/streams/${livestream.id}/go-live`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitting(false)
    }
  }

  return (
    <div className="create-stream">
      <h1>Schedule a stream</h1>
      <form onSubmit={handleSubmit} className="create-form">
        <label>
          <span>Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="e.g. Summer Bag Drop"
          />
        </label>
        <label>
          <span>Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="What viewers should expect"
          />
        </label>
        <label>
          <span>Start time</span>
          <input
            type="datetime-local"
            value={startedAt}
            onChange={(e) => setStartedAt(e.target.value)}
            required
          />
        </label>

        <fieldset className="product-picker">
          <legend>Products to feature</legend>
          {products.length === 0 ? (
            <p className="muted small">No products in this store yet.</p>
          ) : (
            products.map(p => (
              <label key={p.id} className="product-row">
                <input
                  type="checkbox"
                  checked={selectedProductIds.has(p.id)}
                  onChange={() => toggleProduct(p.id)}
                />
                <span>{p.title}</span>
              </label>
            ))
          )}
        </fieldset>

        {error && <p className="error">{error}</p>}

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary">
            {submitting ? 'Creating…' : 'Create & continue to studio'}
          </button>
        </div>
      </form>
    </div>
  )
}
